import { useState, useEffect, useCallback, useRef } from 'react';
import { getOptimizedImageUrl } from '../utils/mediaUtils';

/**
 * Custom hook for managing lightbox functionality
 * @returns {Object} Lightbox state and functions
 */
const useLightbox = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const loadingTimerRef = useRef(null);

  // Open lightbox with selected media
  const openLightbox = useCallback((media, index, mediaArray) => {
    // Save current scroll position
    setScrollPosition(window.pageYOffset);
    
    setCurrentMedia({...media, index, mediaArray});
    setLightboxOpen(true);
    // Prevent scrolling when lightbox is open
    document.body.style.overflow = 'hidden';
    
    // Preload adjacent images for smoother navigation
    if (mediaArray && mediaArray.length > 1) {
      const nextIndex = (index + 1) % mediaArray.length;
      const prevIndex = (index - 1 + mediaArray.length) % mediaArray.length;
      
      // Preload next and previous images with lower quality first
      [nextIndex, prevIndex].forEach(idx => {
        const img = new Image();
        img.src = getOptimizedImageUrl(mediaArray[idx].filePath, 'thumbnail');
      });
    }
  }, []);

  // Close lightbox
  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setCurrentMedia(null);
    // Restore scrolling
    document.body.style.overflow = 'auto';
    
    // Clear any loading timers
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
    
    // Restore scroll position after a short delay to allow rendering
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 50);
  }, [scrollPosition]);

  // Navigate to next/previous image in lightbox with optimized loading
  const navigateLightbox = useCallback((direction) => {
    if (!currentMedia || !currentMedia.mediaArray) return;
    
    // Clear any existing loading timers
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
    
    const { index, mediaArray, mediaType } = currentMedia;
    let newIndex = direction === 'next' ? index + 1 : index - 1;
    
    // Loop back to start/end if needed
    if (newIndex < 0) newIndex = mediaArray.length - 1;
    if (newIndex >= mediaArray.length) newIndex = 0;
    
    // Handle differently based on media type
    if (mediaType === 'image') {
      // Create a temporary loading state for images
      const tempMedia = {...mediaArray[newIndex], index: newIndex, mediaArray, loading: true};
      setCurrentMedia(tempMedia);
      
      // Preload the image and only remove loading state when it's ready
      const img = new Image();
      
      // Add event handlers before setting src
      img.onload = () => {
        // Only update if this is still the current image being loaded
        // This prevents race conditions if user clicks rapidly
        if (currentMedia && currentMedia.index === newIndex) {
          setCurrentMedia(prev => ({...prev, loading: false}));
        }
        
        // Preload the next image in the sequence for smoother navigation
        const nextInSequence = direction === 'next' ? 
          (newIndex + 1) % mediaArray.length : 
          (newIndex - 1 + mediaArray.length) % mediaArray.length;
        
        const nextImg = new Image();
        nextImg.src = getOptimizedImageUrl(mediaArray[nextInSequence].filePath, 'thumbnail');
      };
      
      img.onerror = () => {
        // Handle image loading errors
        console.warn('Failed to load image:', mediaArray[newIndex].filePath);
        if (currentMedia && currentMedia.index === newIndex) {
          setCurrentMedia(prev => ({...prev, loading: false, error: true}));
        }
      };
      
      // Set src after defining onload to ensure the event is captured
      img.src = getOptimizedImageUrl(mediaArray[newIndex].filePath, 'lightbox');
      
      // Add a fallback timeout in case the image load event doesn't fire
      // This prevents the spinner from showing indefinitely if there's an issue
      loadingTimerRef.current = setTimeout(() => {
        if (currentMedia && currentMedia.index === newIndex && currentMedia.loading) {
          console.warn('Image load timed out, forcing display:', mediaArray[newIndex].filePath);
          setCurrentMedia(prev => ({...prev, loading: false}));
        }
      }, 5000); // 5 second fallback (increased from 3 seconds)
    } else {
      // For videos and other media types, just update immediately without loading state
      setCurrentMedia({...mediaArray[newIndex], index: newIndex, mediaArray});
    }
  }, [currentMedia]);

  // Handle image load event from the lightbox component
  const handleLightboxImageLoad = useCallback((id) => {
    if (currentMedia && currentMedia.loading) {
      setCurrentMedia(prev => ({...prev, loading: false}));
      
      // Clear any loading timers
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
  }, [currentMedia]);

  // Handle image error event from the lightbox component
  const handleLightboxImageError = useCallback(() => {
    if (currentMedia) {
      setCurrentMedia(prev => ({...prev, loading: false, error: true}));
      
      // Clear any loading timers
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
  }, [currentMedia]);

  // Retry loading the current lightbox image after an error. Clearing the error
  // remounts the <img> (the error overlay was rendered in its place), which
  // triggers a fresh fetch; onLoad/onError then settle the loading state.
  const retryLightbox = useCallback(() => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
    setCurrentMedia(prev => (prev ? { ...prev, loading: true, error: false } : prev));

    // Safety net so the spinner can't hang if neither load nor error fires.
    loadingTimerRef.current = setTimeout(() => {
      setCurrentMedia(prev => (prev && prev.loading ? { ...prev, loading: false } : prev));
    }, 5000);
  }, []);

  // Touch event handlers for swiping
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  }, []);
  
  const handleTouchEnd = useCallback((e) => {
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    
    // Only register horizontal swipes (ignore small movements and vertical swipes)
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX > 0) {
        // Swipe right - go to previous image
        navigateLightbox('prev');
      } else {
        // Swipe left - go to next image
        navigateLightbox('next');
      }
    }
  }, [navigateLightbox]);

  // Handle keyboard events for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        closeLightbox();
      }
      
      // Add arrow key navigation for images
      if (lightboxOpen && currentMedia && currentMedia.mediaType === 'image' && currentMedia.mediaArray) {
        if (e.key === 'ArrowLeft') {
          navigateLightbox('prev');
        } else if (e.key === 'ArrowRight') {
          navigateLightbox('next');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      
      // Clear any loading timers when component unmounts
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [lightboxOpen, currentMedia, navigateLightbox, closeLightbox]);

  // Safety net: if the gallery unmounts (e.g. route change) while the lightbox
  // is open, make sure body scrolling is restored — closeLightbox may not run.
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return {
    lightboxOpen,
    currentMedia,
    openLightbox,
    closeLightbox,
    navigateLightbox,
    handleTouchStart,
    handleTouchEnd,
    handleLightboxImageLoad,
    handleLightboxImageError,
    retryLightbox
  };
};

export default useLightbox; 